import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Building2 } from 'lucide-react'
import { authApi } from '@/api/auth'
import { useAuthStore } from '@/stores/authStore'

const slugify = (s: string) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50)

export default function Register() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()

  const [orgName, setOrgName] = useState('')
  const [slug, setSlug] = useState('')
  const [slugEdited, setSlugEdited] = useState(false)
  const [adminName, setAdminName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const onOrgName = (v: string) => {
    setOrgName(v)
    if (!slugEdited) setSlug(slugify(v))
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) return setError('Password must be at least 8 characters')
    if (password !== confirm) return setError('Passwords do not match')
    setLoading(true)
    try {
      const res = await authApi.registerOrg({
        org_name: orgName.trim(),
        org_slug: slug.trim(),
        admin_name: adminName.trim(),
        admin_email: email.trim().toLowerCase(),
        admin_password: password,
      })
      const u = res.user
      setAuth(
        { id: u.id, email: u.email, name: u.name, role: u.role as any, timezone: u.timezone, avatar_url: u.avatar_url, org_id: u.org_id, org_slug: u.org_slug },
        res.access_token,
        u.org_slug,
      )
      navigate(u.org_slug ? `/${u.org_slug}` : '/')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40'

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-sm ring-1 ring-black/5">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-[#EC5B2C]/10">
            <Building2 className="h-6 w-6 text-[#EC5B2C]" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Register your organization</h1>
          <p className="mt-1 text-sm text-gray-500">Create your workspace and its first admin account.</p>
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Organization name</label>
            <input className={inputCls} value={orgName} onChange={(e) => onOrgName(e.target.value)} placeholder="Shamoon Butt" required />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Workspace URL</label>
            <div className="flex items-center gap-1 text-sm">
              <span className="whitespace-nowrap text-gray-400">/{''}</span>
              <input className={inputCls} value={slug} onChange={(e) => { setSlug(slugify(e.target.value)); setSlugEdited(true) }} placeholder="shamoon-butt" required />
            </div>
            <p className="mt-1 text-xs text-gray-400">3–50 chars: lowercase letters, numbers, hyphens.</p>
          </div>

          <hr className="border-gray-100" />

          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Your name (admin)</label>
            <input className={inputCls} value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Shamoon Butt" required />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Admin email</label>
            <input type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Password</label>
              <input type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 8 chars" required minLength={8} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Confirm</label>
              <input type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter" required minLength={8} />
            </div>
          </div>

          <button type="submit" disabled={loading} className="w-full rounded-lg bg-[#EC5B2C] py-2.5 text-sm font-semibold text-white hover:bg-[#EA4913] disabled:opacity-60">
            {loading ? 'Creating…' : 'Create organization'}
          </button>
        </form>

        <p className="mt-4 text-center text-sm text-gray-500">
          Already have an account? <Link to="/login" className="font-medium text-[#EC5B2C] hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
